import { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { 
  Sparkles, 
  Wand2, 
  Palette, 
  Pencil, 
  Upload, 
  Loader2, 
  Download,
  ArrowLeft,
  Image as ImageIcon,
  Shuffle
} from "lucide-react";
import { cn } from "@/lib/utils";

type RemixMode = "variation" | "style" | "edit";

const styles = [
  { id: "cartoon", name: "Cartoon", icon: "🎨" },
  { id: "anime", name: "Anime", icon: "🎌" },
  { id: "painting", name: "Peinture", icon: "🖼️" },
  { id: "watercolor", name: "Aquarelle", icon: "💧" },
  { id: "cyberpunk", name: "Cyberpunk", icon: "🌆" },
  { id: "vintage", name: "Vintage", icon: "📷" },
  { id: "minimalist", name: "Minimaliste", icon: "◻️" },
  { id: "sketch", name: "Croquis", icon: "✏️" },
];

const REMIX_CREDITS = 7;

const Remix = () => {
  const { user } = useAuth();
  const { balance } = useCredits();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [sourceImage, setSourceImage] = useState<string>("");
  const [mode, setMode] = useState<RemixMode>("variation");
  const [selectedStyle, setSelectedStyle] = useState("cartoon");
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  // Get image from navigation state
  useEffect(() => {
    const state = location.state as { imageUrl?: string; prompt?: string } | null;
    if (state?.imageUrl) {
      setSourceImage(state.imageUrl);
    }
    if (state?.prompt) {
      setPrompt(state.prompt);
    }
  }, [location.state]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 10 Mo");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSourceImage(event.target?.result as string);
      setGeneratedImage(null);
    };
    reader.readAsDataURL(file);
  };

  const handleRemix = async () => {
    if (!user) {
      toast.error("Connectez-vous pour remixer une image");
      return;
    }

    if (!sourceImage) {
      toast.error("Sélectionnez une image à remixer");
      return;
    }

    if (balance < REMIX_CREDITS) {
      toast.error(`Crédits insuffisants (${REMIX_CREDITS} requis)`);
      navigate("/credits");
      return;
    }

    if (mode === "edit" && !prompt.trim()) {
      toast.error("Décrivez les modifications à apporter");
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/remix-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            image_url: sourceImage,
            mode,
            style: mode === "style" ? selectedStyle : undefined,
            prompt: prompt.trim() || undefined,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erreur lors du remix");
      }

      setGeneratedImage(data.image_url);
      queryClient.invalidateQueries({ queryKey: ["user-credits"] });
      toast.success("Image remixée avec succès !");
    } catch (error) {
      console.error("Remix error:", error);
      toast.error(error instanceof Error ? error.message : "Erreur lors du remix");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;

    const link = document.createElement("a");
    link.href = generatedImage;
    link.download = `floop-remix-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRemixAgain = () => {
    setSourceImage(generatedImage || sourceImage);
    setGeneratedImage(null);
  };

  return (
    <AppLayout title="Remix">
      <div className="p-6 max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
            <Shuffle className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">Remix Studio</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            Transformez vos images avec l'IA
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Créez des variations, changez de style ou éditez vos images avec l'intelligence artificielle.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Source & Controls */}
          <div className="space-y-6">
            {/* Source Image */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Image source
              </h2>

              {sourceImage ? (
                <div className="relative aspect-square rounded-xl overflow-hidden bg-secondary">
                  <img
                    src={sourceImage}
                    alt="Source"
                    className="w-full h-full object-contain"
                  />
                  <button
                    onClick={() => {
                      setSourceImage("");
                      setGeneratedImage(null);
                    }}
                    className="absolute top-2 right-2 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors bg-secondary/30">
                  <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                  <span className="text-foreground font-medium">Importer une image</span>
                  <span className="text-sm text-muted-foreground">ou glisser-déposer</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Mode Selection */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Mode de remix</h2>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setMode("variation")}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
                    mode === "variation"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <Wand2 className={cn("w-6 h-6", mode === "variation" ? "text-primary" : "text-muted-foreground")} />
                  <span className="text-sm font-medium text-foreground">Variation</span>
                </button>
                <button
                  onClick={() => setMode("style")}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
                    mode === "style"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <Palette className={cn("w-6 h-6", mode === "style" ? "text-primary" : "text-muted-foreground")} />
                  <span className="text-sm font-medium text-foreground">Style</span>
                </button>
                <button
                  onClick={() => setMode("edit")}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
                    mode === "edit"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <Pencil className={cn("w-6 h-6", mode === "edit" ? "text-primary" : "text-muted-foreground")} />
                  <span className="text-sm font-medium text-foreground">Édition</span>
                </button>
              </div>
            </div>

            {/* Style Selection (if style mode) */}
            {mode === "style" && (
              <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
                <h2 className="text-lg font-semibold text-foreground">Choisir un style</h2>
                <div className="grid grid-cols-4 gap-2">
                  {styles.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedStyle(s.id)}
                      className={cn(
                        "flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all",
                        selectedStyle === s.id
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <span className="text-2xl">{s.icon}</span>
                      <span className="text-xs font-medium text-foreground">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Prompt Input */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground">
                {mode === "edit" ? "Décrivez les modifications" : "Instructions additionnelles (optionnel)"}
              </h2>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  mode === "edit"
                    ? "Ex: Ajoute un chapeau rouge, change le fond en plage..."
                    : "Ex: Rendre plus lumineux, ajouter plus de détails..."
                }
                className="min-h-[100px] bg-secondary/50 border-border"
              />
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleRemix}
              disabled={!sourceImage || isGenerating || (mode === "edit" && !prompt.trim())}
              className="w-full h-14 bg-gradient-floop hover:opacity-90 text-white rounded-xl text-lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Remix en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Remixer ({REMIX_CREDITS} crédits)
                </>
              )}
            </Button>
          </div>

          {/* Right: Result */}
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Résultat
              </h2>

              <div className="aspect-square rounded-xl overflow-hidden bg-secondary/30 flex items-center justify-center">
                {isGenerating ? (
                  <div className="text-center space-y-4">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
                    <p className="text-muted-foreground">Création de votre remix...</p>
                  </div>
                ) : generatedImage ? (
                  <img
                    src={generatedImage}
                    alt="Remixed"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Le résultat apparaîtra ici</p>
                  </div>
                )}
              </div>

              {generatedImage && (
                <div className="flex gap-3">
                  <Button
                    onClick={handleDownload}
                    variant="secondary"
                    className="flex-1 rounded-xl"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                  <Button
                    onClick={handleRemixAgain}
                    className="flex-1 bg-gradient-floop hover:opacity-90 text-white rounded-xl"
                  >
                    <Shuffle className="w-4 h-4 mr-2" />
                    Re-remixer
                  </Button>
                </div>
              )}
            </div>

            {/* Credits Info */}
            <div className="bg-secondary/30 rounded-xl p-4 text-center">
              <p className="text-sm text-muted-foreground">
                Crédits disponibles: <span className="font-bold text-foreground">{balance}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Remix;
